import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormSelectValues, NavBarMenuItem } from 'src/interfaces/utility-interfaces';

import { AppConstants } from 'src/constants/constants';
import { HttpService } from 'src/app/services/http.service';
import { MyAccountService } from 'src/app/services/my-account.service';
import { NavigationService } from '../../../services/navigation.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { PermissionService } from 'src/app/services/permission.service';
import { StorageService } from 'src/app/services/storage.service';
import { Subscription } from 'rxjs';
import { UserPreferenceService } from 'src/app/services/user-preference.service';
import { UtilService } from 'src/app/services/util.service';
import { fhirR4 } from '@smile-cdr/fhirts';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit, OnDestroy {

  logo = 'assets/logos/CorHealth-OH-Eng-Pos-RGB.png';
  loggedIn: boolean;
  username: string;
  facilityName: Promise<string> | null = null;
  profilesList: FormSelectValues[] = [];
  organizationMap = {};
  userRole: string;
  userOrgID: string;
  userProfileId: string;
  // Default to Patient Search Page
  defaultHomePageUrl: string = NavigationService.patientSearchUrl;
  defaultHomePageSubscription$: Subscription;

  navbarMenuItems: NavBarMenuItem[] = [
    { text: 'Search', active: false, disabled: false, pageUrl: '/patient/search' },
    { text: 'Dashboard', active: false, disabled: false, pageUrl: '/dashboard' },
    { text: 'Reports', active: false, disabled: false, pageUrl: NavigationService.reportingUrl },
    { text: 'Support', active: false, disabled: false, pageUrl: '/support' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private myAccountService: MyAccountService,
    private navigationService: NavigationService,
    private oauthService: OAuthService,
    private httpService: HttpService,
    private utilService: UtilService,
    private storageService: StorageService,
    private userPreferenceService: UserPreferenceService,
    private permissionService: PermissionService
  ) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      for (const item of this.navbarMenuItems) {
        if (item.pageUrl === event.url || (item.dropdownItems && event.url.includes(item.text.toLowerCase()))) {
          item.active = true;
        } else {
          item.active = false;
        }
      }
    });
  }
  ngOnDestroy(): void {
    if (this.defaultHomePageSubscription$) {
      this.defaultHomePageSubscription$.unsubscribe();
      this.defaultHomePageSubscription$ = null;
    }
  }

  ngOnInit() {
    this.route.params.subscribe(routeParam => {
      if (routeParam.app === 'app') {
        this.logout();
      }
    });

    if (this.oauthService.hasValidAccessToken()) {
      this.loggedIn = true;
      localStorage.setItem('userLoggedIn', 'true' );
      this.setUserInfo();
      this.checkSwitchProfile();
    } else {
      this.loggedIn = false;
    }

    this.defaultHomePageSubscription$ = this.userPreferenceService.defaultHomePageChanged.subscribe((defaultPageCode) => {
      this.defaultHomePageUrl = this.navigationService.getDefaultHomePageByCode(defaultPageCode);
    });

    // At profile selection page, this component hasn't been rendered yet
    const pageCode = this.userPreferenceService.parseDefaultHomePageCode( this.userPreferenceService.userPreferenceSettings);
    this.defaultHomePageUrl = this.navigationService.getDefaultHomePageByCode(pageCode);
  }

  checkSwitchProfile() {
    const identityClaims: any = this.oauthService.getIdentityClaims();
    this.myAccountService.getUserProfilesAlongWithRelatedData(identityClaims.sub).then((data: fhirR4.Bundle) => {
      if (data.entry && data.entry.length > 0) {
        const practitionerRolesList: fhirR4.PractitionerRole[] = [];

        data.entry.forEach((entry: fhirR4.BundleEntry) => {
          switch (entry.resource.resourceType) {
            case 'PractitionerRole':
              const practitionerRole = entry.resource as fhirR4.PractitionerRole;
              practitionerRolesList.push(practitionerRole);
              break;
            case 'Organization':
              const organization = entry.resource as fhirR4.Organization;
              this.organizationMap[organization.id] = organization;
              break;
          }
        });

        practitionerRolesList.forEach((practitionerRole: fhirR4.PractitionerRole) => {
          this.profilesList.push(this.processPactitionerRole(practitionerRole));
        });
      }
    });
  }

  processPactitionerRole(practitionerRole: fhirR4.PractitionerRole): FormSelectValues {
    const organizationID = this.utilService.getIdFromReference(practitionerRole.organization.reference);
    const organization: fhirR4.Organization = this.organizationMap[organizationID];
    const role = this.utilService.getCodeFromCodeableConcept(practitionerRole.code,
      AppConstants.userRoleSystem, 'display');
    return {
      value: practitionerRole.id,
      viewValue: organization.name,
      orgId: organizationID,
      role,
      active: this.userProfileId === practitionerRole.id
    };
  }

  setUserInfo() {
    this.userOrgID = sessionStorage.getItem('userOrgID');
    this.userRole = sessionStorage.getItem('userRole');
    this.userProfileId = sessionStorage.getItem('userProfileID');

    this.facilityName = new Promise<string>((resolve, reject) => {
      this.httpService.getResourceWithTypeAndId('Organization', this.userOrgID).then((organization: fhirR4.Organization) => {
        resolve(organization.name);
      });
    });
    const identityClaims: any = this.oauthService.getIdentityClaims();
    this.username = identityClaims.given_name + ' ' + identityClaims.family_name;
  }

  saveProfileIDInSession(selectedProfileID: string, selectedOrgId: string, selectedRole: string) {
    this.storageService.setStorageItem({ key: 'userOrgID', value: selectedOrgId });
    this.storageService.setStorageItem({ key: 'userRole', value: selectedRole });
    // Make this update as the last one, each setStorageItem would trigger StorageChanged event,
    // Subscriber should only filter this last key
    this.storageService.setStorageItem({ key: 'userProfileID', value: selectedProfileID });
  }

  selectProfile(profile: FormSelectValues) {
    if (!profile.active) {
      this.deactivateOtherProfiles();
      profile.active = true;
      this.saveProfileIDInSession(profile.value, profile.orgId, profile.role);
      this.setUserInfo();
    }
  }

  deactivateOtherProfiles() {
    for (const profile of this.profilesList) {
      profile.active = false;
    }
  }

  redirectToGivenPage(page: string) {
    if (page) {
      this.navigationService.navigateToPage(page);
    }
  }

  checkLinkItem(itemLink: string) {
    if (itemLink === NavigationService.manageDocumentLink &&
      this.permissionService.iHavePermission(PermissionService.Admin_Manage_Documents_Permission)) {
      return true;
    }
    if (itemLink === NavigationService.manageNotificationLink &&
      this.permissionService.iHavePermission(PermissionService.Admin_Manage_Notification_Permission)) {
      return true;
    }
    return false;
  }

  logout(){
    this.navigationService.appLogout(false);
  }

}
